import axios, { type AxiosInstance } from 'axios';
import i18n from 'i18next';

import { useCustomerAuthStore } from './customerAuthStore.js';

/**
 * Public storefront API client (/api/v1/storefront/*). Anonymous by default; when
 * a customer session exists their bearer token rides along so catalog reads hit
 * the server's optionalCustomerAuth and return the customer's APPROVED price-list
 * prices (guests and expired tokens just get base prices — fail-open). The fourth
 * client alongside api() (staff), platformApi(), customerApi().
 */
let instance: AxiosInstance | undefined;

const rawBase = import.meta.env.VITE_API_BASE_URL || '/api/v1';
// Origin used to absolutize server-relative asset paths (logo/product images
// arrive as `/api/v1/storefront/:slug/media/:id`, but <img src> needs the API
// origin when the base URL is absolute, e.g. http://localhost:3000/api/v1).
const apiOrigin = /^https?:\/\//.test(rawBase) ? new URL(rawBase).origin : '';

/** Base URL of the public storefront API — for raw fetch() calls (SSE streaming) that can't go through axios. */
export const storefrontApiBase = `${rawBase}/storefront`;

export function publicApi(): AxiosInstance {
  if (instance) return instance;
  instance = axios.create({
    baseURL: `${rawBase}/storefront`,
    headers: { 'Content-Type': 'application/json' },
  });
  // Tag every storefront request with the active content locale so the API
  // returns translated content (falling back to the default locale server-side).
  instance.interceptors.request.use((config) => {
    const lng = (i18n.language || 'en').split('-')[0];
    // Any enabled content language (server falls back to the default locale).
    if (lng) config.params = { ...(config.params ?? {}), locale: lng };
    // Personalized pricing: a signed-in customer's token makes /products reads
    // return their price-list prices. Missing/expired token ⇒ guest prices.
    const token = useCustomerAuthStore.getState().accessToken;
    if (token && !config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
  return instance;
}

/** Absolutize a server-relative asset path for use in <img src>. */
export function assetUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (/^https?:\/\//.test(path)) return path;
  return `${apiOrigin}${path}`;
}
